import React, { useRef } from "react";
import { ICalendarTaskItem } from "ui/Scripts/source/models/calendarTask/calendarTaskItem";
import { Base } from "ui/Scripts/source/framework/base";
import { CalendarTaskCategoryCol } from "./calendarTaskCategoryCol";
import { Translations } from "ui/Scripts/source/models/translations";
import moment from "moment";

interface ICalendarTaskListLineProps {
    item: ICalendarTaskItem;
    selected: boolean;
    first: boolean;
    isLastOpenedEvent: boolean;
    hideDate?: boolean;
    recordListMode?: boolean;
    onClick: (id: string) => void;
    onLongPress: (id: string) => void;
    onRefreshList: () => void;
    onMoveToNextWeek: () => void;
    onMoveToPreviousWeek: () => void;
}

const getTimeStr = (event: ICalendarTaskItem) => {
    if (event.allDay) {
        return Translations.AllDay;
    }
    if (!event.endDate) {
        return Base.timeToTimeStr(event.startDate);
    }
    const mStartDate = moment(event.startDate);
    const mEndDate = moment(event.endDate);
    if (mStartDate.isSame(mEndDate)) {
        return `- ${Base.timeToTimeStr(event.endDate)}`;
    }
    if (mStartDate.isSame(mEndDate, "day")) {
        return `${Base.timeToTimeStr(event.startDate)} - ${Base.timeToTimeStr(event.endDate)}`;
    }
    return `${Base.timeToTimeStr(event.startDate)} - `;
};

export const CalendarTaskListLine = (props: ICalendarTaskListLineProps) => {
    const item = props.item;
    const pressTimer = useRef(0);
    const startX = useRef(0);
    const startY = useRef(0);

    const clearPressTimer = () => {
        clearTimeout(pressTimer.current);
        pressTimer.current = 0;
    };

    const handleLinePress = (ev) => {
        //Get coordinates
        let x: number;
        let y: number;
        if (ev.targetTouches) {
            // Prefer Touch Events
            x = ev.targetTouches[0].clientX;
            y = ev.targetTouches[0].clientY;
        } else {
            // Either Mouse event or Pointer Event
            x = ev.clientX;
            y = ev.clientY;
        }
        startX.current = x;
        startY.current = y;
        //Set timer
        pressTimer.current = window.setTimeout(() => {
            //long press
            clearPressTimer();
            if (props.onLongPress) {
                props.onLongPress(props.item.id);
            }
        }, appConfig.longPressMs);
    };

    const handleLineRelease = (ev) => {
        //short press
        if (pressTimer.current > 0) {
            clearPressTimer();
            //Calculate finger movement
            let lastX = 0;
            let lastY = 0;
            if (ev.changedTouches && ev.changedTouches.length > 0) {
                lastX = ev.changedTouches[ev.changedTouches.length - 1].clientX;
                lastY = ev.changedTouches[ev.changedTouches.length - 1].clientY;
            } else if (ev.clientX) {
                lastX = ev.clientX;
                lastY = ev.clientY;
            }
            const yDiff = startY.current - lastY;
            const xDiff = startX.current - lastX;
            if (props.onMoveToNextWeek && props.onMoveToPreviousWeek && Math.abs(xDiff) > 150 && Math.abs(yDiff) < 75) {
                if (xDiff > 0) { //finger moved left
                    props.onMoveToNextWeek();
                } else if (xDiff < 0) { //finger moved right
                    props.onMoveToPreviousWeek();
                }
            } else if (Math.abs(yDiff) < 10) {
                props.onClick(props.item.id);
            } else if (props.onRefreshList && props.first && Math.abs(yDiff) > 30 && yDiff < 0) { //First item and finger moved down more than x pixels
                props.onRefreshList();
            }
        }
    };

    return (
        <div className={"line css s" + item.state.toString(10) + (props.selected ? " selected" : "") + (props.isLastOpenedEvent ? " loe" : "")}
            onTouchStart={handleLinePress}
            onTouchEnd={handleLineRelease}
            onMouseDown={handleLinePress}
            onMouseUp={handleLineRelease}
            onMouseLeave={handleLineRelease}
        >
            <div className="row">
                <div className="col col-9 bold">{item.title}</div>
                {!props.hideDate
                    ? <div className="col col-3 ra">{Base.timeToDateStr(item.startDate)}</div>
                    : <div className="col col-3 ra"></div>
                }
            </div>
            <div className="row sub">
                <div className="col col-5">{props.recordListMode
                    ? Base.getTimeRangeStr(item.startDate, item.endDate, !item.allDay) + (item.allDay ? ` (${Translations.AllDay.toLocaleLowerCase()})` : " ")
                    : getTimeStr(item)}</div>
                <div className="col col-7">
                    <CalendarTaskCategoryCol item={item} />
                </div>
            </div>
        </div>
    );
};
